import Logger, { captureMessage } from '../logging/logger';
import { getCommonElementParameters, triggerTWCEvent, TWCEventName, TWCVisibilityLogEvent } from '../analytics/twc/events';
import { isVisible } from '../dom/visibility';
import { parseBool } from '../strconv/boolean';
import { activityEvents, trackInactivity } from './inactivity';
import { onceAny } from '../events/once';
import { elementIsOrHasFixedPosition } from '../dom/position';

const trackingElementSelector = '[data-visibility-tracking-id]';

const startEvents: string[] = [
    'twc:ai-photos:activated',
    'twc:ai-photos:deactivated',
    'twc:ai-photos:toggle',
    'twc:slider:init',
];

const flushEvents: string[] = [
    'pagehide',
    'pageshow',
    'twc:popup:open',
    'twc:popup:close',
];

const visibilityTrackingTimeoutDuration: number = 0;

const minVisibilityDurationToConsider: number = 200; // ms

const maxVisibilityDurationToConsider: number = 300_000; // 5 minutes

export enum StopTrackingReason {
    Inactivity = 'inactivity',
    Mutation = 'mutation',
    TrackableTimeout = 'trackable_timeout',
    TrackableFlush = 'trackable_flush',
    TrackableVisibility = 'trackable_visibility',
}

export class VisibilityTracker {
    _container: Document | HTMLElement;
    _intersectionObserver: IntersectionObserver;
    _mutationObserver: MutationObserver;
    trackedElements: Array<TrackableElement> = [];
    flushEnabled: boolean = true;
    trackingTimeoutDuration: number = visibilityTrackingTimeoutDuration;

    constructor( container: Document | HTMLElement = document ) {
        this._container = container;

        this._intersectionObserver = new IntersectionObserver( this._onIntersection.bind( this ), {
            threshold: 0.80,
        } );

        this._mutationObserver = new MutationObserver( this._onMutation.bind( this ) );

        document.addEventListener( 'visibilitychange', ( event ) => {
            if ( document.visibilityState === 'hidden' ) {
                this.flush( true, true, event );
            }
        } );

        flushEvents.forEach( ( eventName: string ) => {
            document.addEventListener( eventName, ( event: Event ) => {
                this.flush( true, true, event );
            } );
        } );

        startEvents.forEach( ( eventName: string ) => {
            window.addEventListener( eventName, this.start.bind( this ) );
        } );

        this._container.addEventListener( 'twc:visibilitytracking:elementtimeout', ( event: Event ) => {
            this.flush( true, false, event );
        } );
    }

    _onMutation( mutations: MutationRecord[], observer: MutationObserver ): void {
        Logger.info( '[visibility_tracking] mutation', mutations );

        let removedTrackables: Array<HTMLElement> = [];

        mutations.forEach( ( mutation: MutationRecord ) => {
            if ( mutation.removedNodes.length === 0 ) {
                return;
            }

            Array.from( mutation.removedNodes ).filter( ( removedNode ) => {
                return removedNode instanceof HTMLElement;
            } ).forEach( ( removedElement: HTMLElement ) => {
                removedTrackables = [
                    ...removedTrackables,
                    ...getTrackableElementsFromRemovedElement( removedElement ),
                ];
            } );

            if ( removedTrackables.length === 0 ) {
                return;
            }
        } );

        removedTrackables.forEach( ( element: HTMLElement ) => {
            const trackable = this.trackedElements.find( ( trackable ) => trackable.element === element );
            const attrMatch = this.trackedElements.find( ( trackable ) => elementTrackingMatches( trackable.element, element ) );

            if ( !trackable && attrMatch && elementIsOrHasFixedPosition( element ) ) {
                return;
            }

            if ( !trackable ) {
                Logger.info( '[visibility_tracking] ignored removed untracked element', {
                    element: getElementTrackingInfo( element ),
                    matchingTrackedElement: !!attrMatch,
                    observed: element.dataset.visibilityTrackingObserved,
                } );
                return;
            }

            trackable.stop( StopTrackingReason.Mutation, window.performance.now() );

            this._intersectionObserver.unobserve( element );
        } );

        this.flush( true );
    }

    _onIntersection( entries: IntersectionObserverEntry[] ): void {
        Logger.info( '[visibility_tracking] intersection', entries );

        entries.forEach( ( entry: IntersectionObserverEntry, index: number ) => {
            this.handleIntersectionEntry( entry, index );
        } );
    }

    _onInactivity(): void {
        Logger.info( '[visibility_tracking] stopping due to inactivity' );

        this.stop( StopTrackingReason.Inactivity );

        onceAny( document, activityEvents, ( event: Event ) => {
            this.start();
        } );
    }

    handleIntersectionEntry( entry: IntersectionObserverEntry, index: number ): void {
        const trackable = this.trackedElements.find( ( trackable ) => trackable.element === entry.target );

        if ( !trackable ) {
            Logger.warn(  `no trackable element found for observed element [${index}] ${JSON.stringify( getElementTrackingInfo( entry.target as HTMLElement ) )}`, entry );
            return;
        }

        trackable.onIntersect( entry );
    }

    start(): void {
        this.releaseTrackables();

        let elements = Array.from( this._container.querySelectorAll<HTMLElement>( trackingElementSelector ) );

        elements = elements.filter( ( element ) => !parseBool( element.dataset.visibilityTrackingObserved ) );

        Logger.info( '[visibility_tracking] starting', elements );

        if ( elements.length === 0 ) {
            return;
        }

        const newElements = elements.map( ( element: HTMLElement ) => {
            return TrackableElement.fromElement( element );
        } );

        this.trackedElements = [
            ...this.trackedElements,
            ...newElements,
        ];

        newElements.forEach( ( trackable: TrackableElement ): void => {
            this._intersectionObserver.observe( trackable.element );

            if ( trackable.element.dataset.visibilityTrackingContainer && trackable.element.closest( trackable.element.dataset.visibilityTrackingContainer ) ) {
                this._mutationObserver.observe( trackable.element.closest( trackable.element.dataset.visibilityTrackingContainer ) as HTMLElement, {
                    childList: true,
                    subtree: true,
                } );
            }

            trackable.element.dataset.visibilityTrackingObserved = 'true';
        } );

        trackInactivity( 'element_visibility', 30, this._onInactivity.bind( this ) );
    }

    _disconnect(): void {
        this._intersectionObserver.disconnect();
        this._mutationObserver.disconnect();

        this.trackedElements.forEach( ( trackable: TrackableElement ): void => {
            trackable.element.dataset.visibilityTrackingObserved = 'false';
        } );
    }

    stop( reason: StopTrackingReason ): void {
        this.trackedElements.forEach( ( trackable: TrackableElement ) => {
            trackable.stop( reason, window.performance.now() );
        } );

        this._disconnect();

        this.flush( true );
    }

    releaseTrackables(): void {
        const releasable = this.trackedElements.filter( ( trackable: TrackableElement ) => !document.documentElement.contains( trackable.element ) );

        releasable.forEach( ( trackableToBeReleased: TrackableElement ) => {
            this._intersectionObserver.unobserve( trackableToBeReleased.element );
        } );

        this.trackedElements = this.trackedElements.filter( ( trackable: TrackableElement ) => {
            return !releasable.find( ( trackableToBeReleased: TrackableElement ) => {
                return trackable.element === trackableToBeReleased.element;
            } );
        } );
    }

    flush( triggerFlushedEvent?: boolean, stopTrackables?: boolean, event?: Event ) {
        if ( !this.flushEnabled ) {
            return [];
        }

        let logs: VisibilityLog[] = [];
        let logEvents: TWCVisibilityLogEvent[] = [];

        this.trackedElements.forEach( ( trackable ) => {
            const flushed = trackable.flush( stopTrackables );

            logs = [
                ...logs,
                ...flushed,
            ];

            const commonEventParams = getCommonElementParameters( trackable.element );

            logEvents = [
                ...logEvents,
                ...flushed.map( ( log: VisibilityLog ) => {
                    return {
                        ...commonEventParams,
                        id: log.id,
                        group: log.tags[ 'group' ] || '',
                        index: parseInt( log.tags[ 'index' ] || '-1' ),
                        duration: log.duration,
                    } as TWCVisibilityLogEvent;
                } ),
            ];
        } );

        if ( logs.length === 0 ) {
            return [];
        }

        Logger.info( '[visibility_tracking] flushing', {
            event,
            triggerFlushedEvent,
            logs,
        } );

        logEvents.forEach( ( event: TWCVisibilityLogEvent ) => {
            triggerTWCEvent<TWCVisibilityLogEvent>( TWCEventName.VisibilityLog, event );
        } );

        this.releaseTrackables();

        if ( triggerFlushedEvent && logs.length > 0 ) {
            this._container.dispatchEvent( new CustomEvent<LogFlushEvent>( 'twc:visibilitytracking:flushed', {
                bubbles: true,
                detail: {
                    logs: logs,
                },
            } ) );
        }

        return logs;
    }
}

type LogFlushEvent = {
    logs: Array<VisibilityLog>;
}

export type VisibilityLog = {
    id: string;
    tags: Record<string, any>;
    duration: number;
}

type TimeLog = {
    from: number;
    to: number
}

class TrackableElement {
    element: HTMLElement;
    id: string = '';
    tags: Record<string, any> = {};

    logs: Array<TimeLog> = [];
    currentLog: TimeLog | null = null;

    timeoutDuration: number = visibilityTrackingTimeoutDuration;
    _timeout: number | null = null;

    constructor( element: HTMLElement ) {
        this.element = element;
        this.id = getTrackingIDFromElement( element );
        this.tags = getTrackingTagsFromElement( element );
    }

    onIntersect( entry: IntersectionObserverEntry ): void {
        if ( isEntryVisibleForTracking( entry ) ) {
            this.start( entry.time );
        } else {
            this.stop( StopTrackingReason.TrackableVisibility, entry.time );
        }
    }

    start( timeRef: DOMHighResTimeStamp = window.performance.now() ): void {
        Logger.info( '[visibility_tracking] starting log', {
            id: this.id,
            ...this.tags,
        } );

        if ( this.currentLog ) {
            this.logs.push( {
                from: this.currentLog.from,
                to: window.performance.now(),
            } );
            this.currentLog = null;
        }

        this.currentLog = {
            from: timeRef,
            to: 0,
        };

        if ( this.timeoutDuration > 0 ) {
            this._timeout = window.setTimeout( this._onTimeout.bind( this ), this.timeoutDuration );
        }
    }

    _onTimeout(): void {
        this.stop( StopTrackingReason.TrackableTimeout, window.performance.now() );
        this.element.dispatchEvent( new CustomEvent( 'twc:visibilitytracking:elementtimeout', { bubbles: true } ) );
    }

    stop( reason: StopTrackingReason, timeRef: DOMHighResTimeStamp = window.performance.now() ): void {
        if ( !this.currentLog ) {
            return;
        }

        Logger.info( '[visibility_tracking] stopping log', {
            reason: reason,
            id: this.id,
            ...this.tags,
        } );

        if ( this._timeout !== null ) {
            window.clearTimeout( this._timeout );
            this._timeout = null;
        }

        const d = timeRef - this.currentLog.from;

        if ( d > maxVisibilityDurationToConsider ) {
            captureMessage( 'excessive visibility tracking duration', {
                reason: reason,
                duration: d,
            } );
        }

        if ( d >= minVisibilityDurationToConsider && d <= maxVisibilityDurationToConsider ) {
            this.logs.push( {
                from: this.currentLog.from,
                to: timeRef,
            } );
        }

        this.currentLog = null;
    }

    flush( stop: boolean = false ): Array<VisibilityLog> {
        if ( stop ) {
            this.stop( StopTrackingReason.TrackableFlush );
        }

        if ( this.logs.length === 0 ) {
            return [];
        }

        const logs = this.logs.map<VisibilityLog>( ( log: TimeLog ) => {
            return {
                id: this.id,
                tags: this.tags || {},
                duration: log.to - log.from,
            };
        } );

        this.logs = [];

        return logs;
    }

    static fromElement( element: HTMLElement ): TrackableElement {
        const trackable = new TrackableElement( element );

        if ( trackable.id === '' ) {
            throw new Error( 'element missing required attribute [data-visibility-tracking-id]' );
        }

        return trackable;
    }
}

function isEntryVisibleForTracking( entry: IntersectionObserverEntry ): boolean {
    if ( !entry.isIntersecting || entry.intersectionRatio <= 0 ) {
        return false;
    }

    if ( entry.target instanceof HTMLElement && !isVisible( entry.target ) ) {
        return false;
    }

    return  true;
}

function getTrackingIDFromElement( element: HTMLElement ): string {
    return element.dataset.visibilityTrackingId || '';
}

function getTrackingTagsFromElement( element: HTMLElement ): Record<string, any> {
    if ( !element.dataset.visibilityTrackingTags ) {
        return {};
    }

    return JSON.parse( element.dataset.visibilityTrackingTags || '{}' );
}

type TrackingInfo = {
    id: string;
    tags: Record<string, any>;
}

function getElementTrackingInfo( element: HTMLElement ): TrackingInfo {
    return {
        id: getTrackingIDFromElement( element ),
        tags: getTrackingTagsFromElement( element ),
    };
}

function getTrackableElementsFromRemovedElement( element: HTMLElement ): HTMLElement[] {
    const trackables = Array.from( element.querySelectorAll<HTMLElement>( trackingElementSelector ) );

    if ( element.matches( trackingElementSelector ) ) {
        return [
            element,
            ...trackables,
        ];
    }

    return trackables;
}

function elementTrackingMatches( elementA: HTMLElement, elementB: HTMLElement ): boolean {
    const infoA = getElementTrackingInfo( elementA );
    const infoB = getElementTrackingInfo( elementB );
    return infoA.id === infoB.id && ( !!infoA.tags[ 'group' ] && !!infoB.tags[ 'group' ] && infoA.tags[ 'group' ] === infoB.tags[ 'group' ] );
}

export function initVisibilityTracking(): void {
    if ( !( 'IntersectionObserver' in window ) || !( 'MutationObserver' in  window ) ) {
        return;
    }

    window.visibilityTracker = new VisibilityTracker();
};
